/**
 * Invalid-date-safe wrappers around `date-fns`.
 *
 * `format(parseISO(x), …)` throws `RangeError: Invalid time value` when `x`
 * is a truthy-but-invalid date string. (Relative time no longer lives here:
 * `safeRelative` wraps `formatTimeAgo` in `i18n/intl.ts`, #3168.) Rendered directly in JSX, that throw blanks the component (and any
 * route without an error boundary). These helpers return a fallback instead,
 * so one malformed backend/agent-written timestamp can't white-screen a page.
 *
 * For the TRFer-specific fixed-format variants see `safeShortDate` /
 * `safeRelativeTime` in `trfer-display.ts` (B-136). This module is the general,
 * app-wide form (B-137).
 */
import { format, parseISO } from 'date-fns';
import { formatPreset, formatTimeAgo, type DatePreset, type PresetOptions } from '@/i18n/intl';

type MaybeIso = string | null | undefined;

/** `format(parseISO(iso), fmt)` when `iso` is a valid date, else `fallback`. */
export function safeFormat(iso: MaybeIso, fmt: string, fallback = '—'): string {
  if (!iso) return fallback;
  let d: Date;
  try { d = parseISO(iso); } catch { return fallback; }
  if (Number.isNaN(d.getTime())) return fallback;
  return format(d, fmt);
}

/**
 * The calendar day a DATE-ONLY value denotes, as a local `Date` at midnight —
 * or `null` when there isn't one.
 *
 * A field backed by `<input type="date">` sends `YYYY-MM-DD`, which Mongo
 * stores as `new Date('2026-08-27')` — UTC midnight. Rendering that instant in
 * the viewer's zone shows the PREVIOUS DAY to everyone west of UTC: a symptom
 * logged for Aug 27 read "Aug 26 · 8:00 PM" in America/New_York (#1308). The
 * same class of defect as B-432 on the staff intake dates.
 *
 * So a date-only value is read for its UTC calendar parts and re-anchored
 * locally, which makes every `date-fns` format token and every
 * `differenceInCalendarDays` comparison operate on the day that was typed.
 * `new Date(v)` (not `parseISO`) is deliberate: it is the parser that reads a
 * bare `YYYY-MM-DD` as UTC, so both storage shapes land on the same day.
 *
 * Use this ONLY for fields that denote a day. A real timestamp — an
 * appointment's `date_time`, a `created_at` — must keep being rendered in the
 * reader's zone, and belongs on `safeFormat`.
 */
export function parseDateOnly(v: string | Date | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** `format()` of a date-only value on its stored calendar day, else `fallback`. */
export function dateOnlyLabel(
  v: string | Date | null | undefined,
  fmt: string,
  fallback = '—',
): string {
  const d = parseDateOnly(v);
  if (!d) return fallback;
  return format(d, fmt);
}

/**
 * "3 hours ago" when valid, else `fallback`.
 *
 * @deprecated #3168 — a thin wrapper over `formatTimeAgo` (`i18n/intl.ts`),
 * the one relative-time formatter; new code calls that.
 */
export function safeRelative(iso: MaybeIso, fallback = ''): string {
  return formatTimeAgo(iso, { fallback });
}

// ── Intl twins (#2109 R3-B5) ─────────────────────────────────────────────
//
// The same invalid-date safety as `safeFormat` / `dateOnlyLabel`, formatted
// through `Intl` in the UI language with a named preset (`i18n/intl.ts`
// `DATE_PRESETS`: `monthDay` for `MMM d`, `monthDayYear` for `MMM d, yyyy`,
// `longDate`, `weekdayMonthDay`, `monthYear`, `time`, `weekdayTime`,
// `monthDayTime`, `numeric`) or an explicit options object. Parent pages move
// to these; the date-fns originals stay until their last (staff, R4) caller
// moves.

/** `safeFormat`’s Intl twin: an instant in the reader’s zone (or `options.timeZone`), else `fallback`. */
export function safeFormatIntl(
  iso: MaybeIso,
  preset: DatePreset | Intl.DateTimeFormatOptions,
  fallback = '—',
  options: Omit<PresetOptions, 'dateOnly'> = {},
): string {
  if (!iso) return fallback;
  let d: Date;
  try { d = parseISO(iso); } catch { return fallback; }
  if (Number.isNaN(d.getTime())) return fallback;
  return formatPreset(d, preset, options);
}

/** `dateOnlyLabel`’s Intl twin: a date-only value on its stored calendar day (UTC parts, B-432), else `fallback`. */
export function dateOnlyIntl(
  v: string | Date | null | undefined,
  preset: DatePreset | Intl.DateTimeFormatOptions,
  fallback = '—',
  options: Pick<PresetOptions, 'locale'> = {},
): string {
  if (v === null || v === undefined || v === '') return fallback;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return fallback;
  return formatPreset(d, preset, { ...options, dateOnly: true });
}
